import * as THREE from 'three';
import type { SketchFace, SketchVec3 } from './types';

/**
 * Face ⇄ face intersection (Sketch's Intersect Faces).
 *
 * Two non-parallel planar faces cross along a line — the intersection of
 * their carrier planes. The segment of that line inside BOTH loops is the
 * cut edge each face must be split with. Per face the line is clipped to
 * the spans whose midpoints pass an even-odd inside test over the loops
 * (outer + holes), so concave contours and holes behave.
 *
 * Coplanar faces are skipped: their intersection is a region, not a line —
 * Sketch splits those too, but the segment model cannot represent it.
 */

const EPS = 1e-7;

interface P2 { x: number; y: number }

/** Even-odd inside test over one projected loop (open loop implied closed). */
function pointInside2D(p: P2, polygon: P2[]): boolean {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const a = polygon[i];
        const b = polygon[j];
        const crosses =
            (a.y > p.y) !== (b.y > p.y) &&
            p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x;
        if (crosses) inside = !inside;
    }
    return inside;
}

interface PlaneFrame {
    origin: THREE.Vector3;
    u: THREE.Vector3;
    v: THREE.Vector3;
    project: (loop: SketchVec3[]) => P2[];
}

/** Orthonormal 2D frame of the face plane (stable reference axis). */
function planeFrame(face: SketchFace): PlaneFrame {
    const origin = new THREE.Vector3(...face.origin);
    const n = new THREE.Vector3(...face.normal).normalize();
    const ref = Math.abs(n.x) < 0.9 ? new THREE.Vector3(1, 0, 0) : new THREE.Vector3(0, 1, 0);
    const u = new THREE.Vector3().crossVectors(ref, n).normalize();
    const v = new THREE.Vector3().crossVectors(n, u).normalize();
    return {
        origin,
        u,
        v,
        project: (loop) =>
            loop.map((p) => {
                const d = new THREE.Vector3(...p).sub(origin);
                return { x: d.dot(u), y: d.dot(v) };
            }),
    };
}

/**
 * Spans of the world line (p0 + t·dir) inside the face: every gap between
 * consecutive loop crossings whose midpoint is inside the even-odd region
 * (outer minus holes). Empty when the line misses the face.
 */
function insideSpans(
    face: SketchFace,
    frame: PlaneFrame,
    p0: THREE.Vector3,
    dir: THREE.Vector3
): Array<[number, number]> {
    const dU = dir.dot(frame.u);
    const dV = dir.dot(frame.v);
    const oU = p0.clone().sub(frame.origin).dot(frame.u);
    const oV = p0.clone().sub(frame.origin).dot(frame.v);

    const crossings: number[] = [];
    const collect = (loop: SketchVec3[]): void => {
        const pts = frame.project(loop);
        for (let i = 0; i < pts.length; i++) {
            const a = pts[i];
            const b = pts[(i + 1) % pts.length];
            const eX = b.x - a.x;
            const eY = b.y - a.y;
            // Edge a + s·e vs line (o + t·d) in 2D: solve the 2x2 system.
            const denom = eX * dV - eY * dU;
            if (Math.abs(denom) < EPS) continue; // edge parallel to the line
            const wX = a.x - oU;
            const wY = a.y - oV;
            const s = (wX * dV - wY * dU) / denom;
            if (s < -EPS || s > 1 + EPS) continue;
            crossings.push((wX * eY - wY * eX) / denom);
        }
    };
    collect(face.outer);
    face.holes.forEach(collect);

    if (crossings.length < 2) return [];
    crossings.sort((x, y) => x - y);

    const spans: Array<[number, number]> = [];
    for (let i = 0; i < crossings.length - 1; i++) {
        const t0 = crossings[i];
        const t1 = crossings[i + 1];
        if (t1 - t0 < EPS) continue;
        const mid = { x: oU + ((t0 + t1) / 2) * dU, y: oV + ((t0 + t1) / 2) * dV };
        const outer = frame.project(face.outer);
        if (!pointInside2D(mid, outer)) continue;
        if (face.holes.some((h) => pointInside2D(mid, frame.project(h)))) continue;
        spans.push([t0, t1]);
    }
    return spans;
}

/**
 * Intersection segment (2 world points) of two planar faces, or null when
 * the faces are parallel, coplanar, or do not overlap.
 */
export function faceIntersectionSegment(a: SketchFace, b: SketchFace): SketchVec3[] | null {
    const na = new THREE.Vector3(...a.normal).normalize();
    const nb = new THREE.Vector3(...b.normal).normalize();
    const dir = new THREE.Vector3().crossVectors(na, nb);
    if (dir.lengthSq() < EPS * EPS) return null; // parallel or coplanar
    dir.normalize();

    // Two-plane line: p(t) = p0 + t·dir with p0 = dA(nB×dir) + dB(dir×nA)
    // (unit cross product), where the planes read nA·p = dA, nB·p = dB.
    const dA = -na.dot(new THREE.Vector3(...a.origin));
    const dB = -nb.dot(new THREE.Vector3(...b.origin));
    const p0 = new THREE.Vector3()
        .addScaledVector(new THREE.Vector3().crossVectors(nb, dir), dA)
        .addScaledVector(new THREE.Vector3().crossVectors(dir, na), dB);

    const frameA = planeFrame(a);
    const frameB = planeFrame(b);
    const spansA = insideSpans(a, frameA, p0, dir);
    const spansB = insideSpans(b, frameB, p0, dir);
    if (spansA.length === 0 || spansB.length === 0) return null;

    // Largest overlap of the two span sets is the cut segment.
    let best: [number, number] | null = null;
    for (const [a0, a1] of spansA) {
        for (const [b0, b1] of spansB) {
            const t0 = Math.max(a0, b0);
            const t1 = Math.min(a1, b1);
            if (t1 - t0 < EPS) continue;
            if (!best || t1 - t0 > best[1] - best[0]) best = [t0, t1];
        }
    }
    if (!best) return null;

    const pa = p0.clone().addScaledVector(dir, best[0]);
    const pb = p0.clone().addScaledVector(dir, best[1]);
    return [
        [pa.x, pa.y, pa.z],
        [pb.x, pb.y, pb.z],
    ];
}
